// src/com/file_of_report/ExcelBuilder.js
import Excel from 'exceljs';
import { AppWord, TableColumnType } from '../../value/AppConst';


const ThinBorderLine = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
};

const HeaderAlignmentCenter = {vertical:'middle', horizontal:AppWord.CENTER};
const FillForTotal = { type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFCCCCCC'}, };
function getAlign(col) {
    if(col.option && col.option.align) {
        return col.option.align;
    }
    if(col.option && col.option.type) {
        const type = col.option.type;
        if(type === TableColumnType.INTEGER || type === TableColumnType.FLOAT) return AppWord.RIGHT;
        if(
            type === TableColumnType.DATETIME || type === TableColumnType.DATE
            || type === TableColumnType.HHMM_TEXT
            || type === TableColumnType.ENUM
            || type === TableColumnType.BOOLEAN
        ) return AppWord.CENTER;
    }
    return AppWord.LEFT;
}

function isNumberType(col) {
    if(col.option && col.option.type) {
        const type = col.option.type;
        return (type === TableColumnType.INTEGER || type === TableColumnType.FLOAT);
    }
    return false;
}

function makeHeaderFill(colorArgb) {
    return { type: 'pattern', pattern: 'solid', fgColor: {argb: colorArgb}, };
}

export default class ExcelBuilder {
    constructor(instructions, records) {
        this.workbook = new Excel.Workbook();
        this.instructions = instructions; // {columns:[], headerRowSpan: 3, title: '', headerRows: [], headerBackgroundColor: null}
        this.records = records;
    }

    addWorksheet(name) {
        this.worksheet = this.workbook.addWorksheet(name);
    }

    build(setProgress) {
        setProgress(0);

        this.addWorksheet('Report');
        const {columns, headerRowSpan, title, headerRows} = this.instructions;
        const ws = this.worksheet;
        const bgColor = this.instructions.headerBackgroundColor || 'FFFFFF00'; // Default to yellow if not provided
        const HeaderFill = makeHeaderFill(bgColor);
        let currentRow = 1;
        // Title
        if(title) {
            // Make it bold.
            const titleRow = ws.getRow(currentRow);
            titleRow.getCell(1).value = title;
            titleRow.font = {size:20, bold:true};
            // ws.mergeCells(currentRow, 1, currentRow, columns.length);
            // titleRow.alignment = {vertical:'middle', horizontal:'center'};

            currentRow++;
        }

        // Header Rows
        for(const header of headerRows) {
            const headerRow = ws.getRow(currentRow);
            headerRow.getCell(1).value = header;
            currentRow++;
        }

        // Column Headers - 여러 행으로 구성된 헤더. colSpan, rowSpan 처리 필요.
        let colIndex = 1;
        const total = {};
        const needTotal = {};

        for(const col of columns) {
            const id = col.id; // data field id
            const label = col.label || id;
            const columnWidth = Math.floor((col.width || 100) / 8);
            ws.getColumn(colIndex).width = columnWidth;

            const headerColumnFill = col.headerBackgroundColor ? makeHeaderFill(col.headerBackgroundColor) : HeaderFill;

            // prepare for totals
            if(isNumberType(col) && !col.noTotal) {
                total[id] = 0;
                needTotal[id] = true;
            }

            let countSuperHeaderRows = 0;
            // superHeaders가 있으면 여러 행을 만들어야 함. headerRowSpan-1 값이 최대 superHeaders 수.
            if(col.superHeaders && col.superHeaders.length > 0) {
                let headerRowNum = currentRow;
                for(let ri=0; ri<(headerRowSpan-1) && ri<col.superHeaders.length; ri++) {
                    countSuperHeaderRows++; // 수퍼헤더 칼럼이 널이어도 행은 하나 추가 카운트 해야 함.
                    const shCol = col.superHeaders[ri];
                    // 각 shr는 1개 이상의 헤더칼럼을 포함할 수 있음. (배열이어야 함!) ===> 아님! 바꿔야...
                    // 각 shCol는 1개의 헤더칼럼임. 즉, 1개 헤더행에 1개의 칼럼이므로 배열이 아님.
                    if(shCol) {
                        // 각 수퍼 헤더칼럼에 대해 merge 하고 값 설정
                        // 먼저 merge 필요 여부 체크
                        const colSpan = shCol.colSpan || 1;
                        const rowSpan = 1; // shCol.rowSpan || 1; - 위험한 시도.
                        if(colSpan > 1 || rowSpan > 1) {
                            ws.mergeCells(
                                headerRowNum,
                                colIndex,
                                headerRowNum + (rowSpan - 1), // 뒤 행도 포함
                                colIndex + (colSpan - 1) // 뒤 칼럼도 포함
                            );
                        }

                        // 값 설정: 병합된 경우에도 셀의 첫 칸에만 값 설정하면 됨.
                        const firstCell = ws.getCell(headerRowNum, colIndex);
                        if(firstCell) {
                            const superHeaderFill = shCol.headerBackgroundColor ? makeHeaderFill(shCol.headerBackgroundColor) : headerColumnFill;
                            firstCell.value = shCol.label || `${label}${headerRowNum}?`;
                            firstCell.alignment = HeaderAlignmentCenter;
                            firstCell.fill = superHeaderFill;
                            firstCell.font = {bold: true};
                            firstCell.border = ThinBorderLine;
                        }
                    }
                    // else - null인 경우: 앞의 수퍼헤더가 colSpan으로 차지한 칼럼만큼 건너뜀. 아무 작업 안함.

                    headerRowNum++; // 다음 헤더 행으로 이동
                }
            }

            /*
             기본 헤더 칼럼 (맨 아래 행):
             기본칼럼은 colSpan, rowSpan값을 사용하지 않음.
             그러나, rowSpan은 수퍼헤더의 유무, 갯수에 따라 계산해서 병합처리 해야 함.
             */
            const rowSpan = headerRowSpan - countSuperHeaderRows;

            if(rowSpan > 1) {
                ws.mergeCells(
                    currentRow + countSuperHeaderRows, // superHeader 다음 행부터 시작
                    colIndex,
                    currentRow + countSuperHeaderRows + (rowSpan - 1), // 뒤 행도 포함
                    colIndex
                );
            }

            const baseHeaderCell = ws.getCell(currentRow + countSuperHeaderRows, colIndex);
            if(baseHeaderCell) {
                baseHeaderCell.value = label;
                baseHeaderCell.alignment = HeaderAlignmentCenter;
                baseHeaderCell.fill = headerColumnFill;
                baseHeaderCell.font = {bold: true};
                baseHeaderCell.border = ThinBorderLine;
            }
            else {
                console.warn('Base header cell not found at row=', currentRow + countSuperHeaderRows, ' col=', colIndex);
            }


            colIndex++; // 다음 칼럼 인덱스로 이동
        } // end of columns + superHeaders columns

        currentRow += headerRowSpan; // 헤더 행 수만큼 증가

        for(const record of this.records) {
            const dataRow = ws.getRow(currentRow);
            let dataColIndex = 1;
            for(const col of columns) {
                const id = col.id;
                const value = record[id];
                const cell = dataRow.getCell(dataColIndex);
                if(cell) {
                    cell.value = col.excel ? col.excel(record, value) : value;
                    cell.alignment = { vertical: 'middle', horizontal: getAlign(col) };
                    cell.border = ThinBorderLine;
                    // number 형식이면 천단위 콤마 처리
                    if(needTotal[id] && value !== null && value !== undefined) {
                        cell.numFmt = '#,##0';
                        // 소수점이 있는 경우가 실제로 있을까?
                    }
                }

                // accumulate totals
                if(needTotal[id]) total[id] += (value || 0);

                dataColIndex++;
            }
            currentRow++;
            const percent = Math.floor(currentRow * 100 / this.records.length);
            setProgress(percent);
        }
        // Totals row
        if(Object.keys(needTotal).length > 0) {
            const totalRow = ws.getRow(currentRow);
            let totalColIndex = 1;

            for(const col of columns) {
                const id = col.id;
                const cell = totalRow.getCell(totalColIndex);
                if(cell) {
                    if(needTotal[id]) {
                        cell.value = total[id];
                        cell.alignment = { vertical: 'middle', horizontal: getAlign(col) };
                        // cell.font = {bold: true};
                        cell.border = ThinBorderLine;
                        cell.fill = FillForTotal;
                        cell.numFmt = '#,##0';
                    }
                    else if(totalColIndex === 1) {
                        // extra special treatment for first column if not number type
                        cell.value = 'Total';
                        cell.alignment = HeaderAlignmentCenter;
                        cell.font = {bold: true};
                        cell.border = ThinBorderLine;
                        cell.fill = FillForTotal;
                    }
                    else {
                        // make it blank
                        cell.value = '';
                        cell.border = ThinBorderLine;
                        cell.fill = FillForTotal;
                    }
                }
                totalColIndex++;
            }
        }
        setProgress(100);
    }

    getBlob = async () => {

        const buf = await this.workbook.xlsx.writeBuffer();

        return new Blob([buf]);
    }

    /* async save(fileName) {
        await this.workbook.xlsx.writeFile(fileName);
    } */
}

/*
수퍼헤더에 colSpan이 있으면 뒤 칼럼은 null로 채워야 함.
*/
